import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Users,
  Activity,
  TrendingUp,
  FileText,
  ShoppingCart,
  Package,
  LifeBuoy,
} from 'lucide-react';
import { useMusteriler } from '@features/musteri';
import { useAktiviteler } from '@features/aktivite';
import { useFirsatlar } from '@features/firsat';
import { useTeklifler } from '@features/teklif';
import { useSiparisler } from '@features/siparis';
import { useUrunler } from '@features/urun';
import { useTalepler } from '@features/destek';
import type { Musteri } from '@/types/musteri';
import type { Aktivite } from '@/types/aktivite';
import type { Firsat } from '@/types/firsat';
import type { Teklif } from '@/types/teklif';
import type { Siparis } from '@/types/siparis';
import type { Urun } from '@/types/urun';
import type { Talep } from '@/types/destek';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Açılışta hazır gelen arama metni (Zo asistanından). */
  initialQuery?: string;
}

type ModuleKey =
  | 'musteri'
  | 'aktivite'
  | 'firsat'
  | 'teklif'
  | 'siparis'
  | 'urun'
  | 'destek';

interface ResultItem {
  module: ModuleKey;
  id: string;
  title: string;
  subtitle: string;
  path: string;
}

const MODULE_META: Record<
  ModuleKey,
  { label: string; Icon: typeof Users }
> = {
  musteri: { label: 'Müşteriler', Icon: Users },
  aktivite: { label: 'Aktiviteler', Icon: Activity },
  firsat: { label: 'Fırsatlar', Icon: TrendingUp },
  teklif: { label: 'Teklifler', Icon: FileText },
  siparis: { label: 'Siparişler', Icon: ShoppingCart },
  urun: { label: 'Ürünler', Icon: Package },
  destek: { label: 'Destek', Icon: LifeBuoy },
};

const MODULE_ORDER: ModuleKey[] = [
  'musteri',
  'aktivite',
  'firsat',
  'teklif',
  'siparis',
  'urun',
  'destek',
];

function musteriAd(m: {
  tipi: 'BIREYSEL' | 'KURUMSAL';
  ad: string;
  soyad: string | null;
  unvan: string | null;
}): string {
  if (m.tipi === 'KURUMSAL') return m.unvan ?? m.ad;
  return [m.ad, m.soyad].filter(Boolean).join(' ');
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('tr-TR');
  } catch {
    return '';
  }
}

export function CommandPalette({ open, onClose, initialQuery }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Modal açılınca state'i sıfırla (Zo'dan gelen hazır sorgu varsa onunla başla), input'a focus.
  useEffect(() => {
    if (open) {
      const q = initialQuery ?? '';
      setQuery(q);
      setDebounced(q.trim());
      setActiveIndex(0);
      // Mount sonrası focus
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, initialQuery]);

  // 250ms debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const enabled = debounced.length >= 2;
  const q = enabled ? debounced : '';
  const perPage = 5;

  // Tüm modüller paralel — react-query enabled flag ile no-op yapamadığımız
  // için ufak bir trick: q boşken hook çağrılır ama biz veriyi UI'da yok sayarız.
  // Daha temizi: enabled olmadığında perPage'ı sabit tut + ID stabil.
  const musteriQ = useMusteriler(enabled ? { q, perPage } : {});
  const aktiviteQ = useAktiviteler({ perPage }); // q desteği yoksa list parametrelerinde — yine de istek atar
  const firsatQ = useFirsatlar(enabled ? { q, perPage } : {});
  const teklifQ = useTeklifler(enabled ? { q, perPage } : {});
  const siparisQ = useSiparisler(enabled ? { q, perPage } : {});
  const urunQ = useUrunler(enabled ? { q, perPage } : {});
  const talepQ = useTalepler(enabled ? { q, perPage } : {});

  const grouped = useMemo(() => {
    if (!enabled) {
      return {} as Record<ModuleKey, ResultItem[]>;
    }
    const out: Record<ModuleKey, ResultItem[]> = {
      musteri: [],
      aktivite: [],
      firsat: [],
      teklif: [],
      siparis: [],
      urun: [],
      destek: [],
    };

    const musteriler = (musteriQ.data?.data ?? []) as Musteri[];
    out.musteri = musteriler.map((m) => ({
      module: 'musteri' as const,
      id: m.id,
      title: musteriAd(m),
      subtitle: m.sehir ?? '',
      path: `/musteriler/${m.id}`,
    }));

    // Aktivite hook'unun ListAktiviteQuery'sinde q yok — frontend tarafında
    // başlık üzerinden filtreleyelim.
    const aktiviteler = (aktiviteQ.data?.data ?? []) as Aktivite[];
    const ql = debounced.toLocaleLowerCase('tr-TR');
    out.aktivite = aktiviteler
      .filter((a) => a.baslik?.toLocaleLowerCase('tr-TR').includes(ql))
      .slice(0, perPage)
      .map((a) => ({
        module: 'aktivite' as const,
        id: a.id,
        title: a.baslik,
        subtitle: [a.tipTanimi?.ad, formatDate(a.tarih)]
          .filter(Boolean)
          .join(' • '),
        path: `/aktiviteler/${a.id}`,
      }));

    const firsatlar = (firsatQ.data?.data ?? []) as Firsat[];
    out.firsat = firsatlar.map((f) => ({
      module: 'firsat' as const,
      id: f.id,
      title: f.baslik,
      subtitle: `${f.tutar} ${f.paraBirimi}`.trim(),
      path: `/firsatlar/${f.id}`,
    }));

    const teklifler = (teklifQ.data?.data ?? []) as Teklif[];
    out.teklif = teklifler.map((t) => ({
      module: 'teklif' as const,
      id: t.id,
      title: `${t.no} — ${t.baslik}`,
      subtitle: t.musteri ? musteriAd(t.musteri as never) : '',
      path: `/teklifler/${t.id}`,
    }));

    const siparisler = (siparisQ.data?.data ?? []) as Siparis[];
    out.siparis = siparisler.map((s) => ({
      module: 'siparis' as const,
      id: s.id,
      title: `${s.no} — ${s.baslik}`,
      subtitle: s.musteri ? musteriAd(s.musteri as never) : '',
      path: `/siparisler/${s.id}`,
    }));

    const urunler = (urunQ.data?.data ?? []) as Urun[];
    out.urun = urunler.map((u) => ({
      module: 'urun' as const,
      id: u.id,
      title: u.ad,
      subtitle: u.kod ?? u.tipi,
      path: `/urunler?q=${encodeURIComponent(u.ad)}`,
    }));

    const talepler = (talepQ.data?.data ?? []) as Talep[];
    out.destek = talepler.map((t) => ({
      module: 'destek' as const,
      id: t.id,
      title: t.baslik,
      subtitle: t.durumTanimi?.ad ?? '',
      path: `/destek/${t.id}`,
    }));

    return out;
  }, [
    enabled,
    debounced,
    musteriQ.data,
    aktiviteQ.data,
    firsatQ.data,
    teklifQ.data,
    siparisQ.data,
    urunQ.data,
    talepQ.data,
  ]);

  // Düz liste — klavye navigasyonu için.
  const flat = useMemo(() => {
    if (!enabled) return [] as ResultItem[];
    const arr: ResultItem[] = [];
    for (const k of MODULE_ORDER) {
      const list = grouped[k] ?? [];
      arr.push(...list);
    }
    return arr;
  }, [enabled, grouped]);

  // Debounced değişince activeIndex sıfırla.
  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  // Aktif öğeyi görüş alanına kaydır.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-cmd-idx="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function handleSelect(item: ResultItem | undefined) {
    if (!item) return;
    onClose();
    navigate(item.path);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length > 0) setActiveIndex((i) => (i + 1) % flat.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length > 0)
        setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(flat[activeIndex]);
    }
  }

  if (!open) return null;

  const isLoading =
    enabled &&
    (musteriQ.isFetching ||
      aktiviteQ.isFetching ||
      firsatQ.isFetching ||
      teklifQ.isFetching ||
      siparisQ.isFetching ||
      urunQ.isFetching ||
      talepQ.isFetching);

  const noResults = enabled && !isLoading && flat.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 pt-[20vh]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Komut paleti"
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Arama Inputu */}
        <div className="relative border-b">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Müşteri, fırsat, teklif… ara"
            className="h-12 w-full bg-transparent pl-11 pr-4 text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>

        {/* Sonuç gövdesi */}
        <div
          ref={listRef}
          className="max-h-[60vh] overflow-y-auto"
        >
          {!enabled && (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              <p>Arama yapmak için yazın…</p>
              <p className="mt-3 text-xs">
                <kbd className="rounded border bg-muted px-1.5 py-0.5">↑</kbd>{' '}
                <kbd className="rounded border bg-muted px-1.5 py-0.5">↓</kbd>{' '}
                gez ·{' '}
                <kbd className="rounded border bg-muted px-1.5 py-0.5">
                  Enter
                </kbd>{' '}
                aç ·{' '}
                <kbd className="rounded border bg-muted px-1.5 py-0.5">Esc</kbd>{' '}
                kapat
              </p>
            </div>
          )}

          {noResults && (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              Sonuç bulunamadı
            </div>
          )}

          {enabled &&
            MODULE_ORDER.map((key) => {
              const items = grouped[key] ?? [];
              if (items.length === 0) return null;
              const { label, Icon } = MODULE_META[key];
              return (
                <div key={key} className="py-1">
                  <div className="flex items-center gap-2 px-4 py-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" />
                    <span>{label}</span>
                    <span className="ml-auto">{items.length}</span>
                  </div>
                  <ul>
                    {items.map((item) => {
                      const idx = flat.findIndex(
                        (f) => f.module === item.module && f.id === item.id,
                      );
                      const isActive = idx === activeIndex;
                      return (
                        <li key={`${item.module}-${item.id}`}>
                          <button
                            type="button"
                            data-cmd-idx={idx}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => handleSelect(item)}
                            className={[
                              'flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left text-sm',
                              isActive ? 'bg-primary/10' : 'hover:bg-muted',
                            ].join(' ')}
                          >
                            <span className="font-medium">{item.title}</span>
                            {item.subtitle && (
                              <span className="text-xs text-muted-foreground">
                                {item.subtitle}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
